import { cn } from '@/lib/cn';

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fv-primary-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--fv-surface)]';
const focusRingDanger =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fv-danger-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--fv-surface)]';
const motion =
  'transition-[color,background-color,border-color,box-shadow,transform] duration-150 motion-reduce:transition-none motion-reduce:active:scale-100';
const press = 'active:scale-[0.98]';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'outline'
  | 'danger'
  | 'dangerSoft'
  | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

export function buttonVariants(opts?: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
}): string {
  const variant = opts?.variant ?? 'primary';
  const size = opts?.size ?? 'md';

  const base = cn(
    'inline-flex shrink-0 items-center justify-center gap-2 font-medium',
    motion,
    press,
    'disabled:pointer-events-none disabled:opacity-50',
    focusRing,
    opts?.fullWidth && 'w-full'
  );

  const sizes: Record<ButtonSize, string> = {
    sm: 'h-8 rounded-[var(--fv-radius)] px-2.5 text-xs',
    md: 'h-10 rounded-[var(--fv-radius)] px-4 text-sm',
    lg: 'h-11 rounded-[var(--fv-radius-lg)] px-5 text-sm'
  };

  const variants: Record<ButtonVariant, string> = {
    primary: cn(
      'bg-[var(--fv-primary)] text-white shadow-[var(--fv-shadow-button)]',
      'hover:bg-[var(--fv-primary-hover)] hover:shadow-[var(--fv-shadow-md)]'
    ),
    secondary: cn(
      'border border-[var(--fv-border)] bg-[var(--fv-surface)] text-[var(--fv-text)] shadow-[var(--fv-shadow-sm)]',
      'hover:border-[var(--fv-primary)]/30 hover:bg-[var(--fv-bg)]'
    ),
    outline: cn(
      'border border-[var(--fv-border)] bg-transparent text-[var(--fv-text)]',
      'hover:border-[var(--fv-primary)]/40 hover:bg-[var(--fv-bg)]'
    ),
    ghost: 'text-[var(--fv-text-muted)] hover:bg-[var(--fv-bg)] hover:text-[var(--fv-text)]',
    danger: cn(
      'bg-[var(--fv-danger)] text-white shadow-[var(--fv-shadow-button)]',
      'hover:bg-[var(--fv-danger-hover)]',
      focusRingDanger
    ),
    dangerSoft: cn(
      'bg-[var(--fv-danger-muted)] text-[var(--fv-danger)]',
      'hover:bg-[var(--fv-danger-muted)] hover:text-[var(--fv-danger-hover)]',
      focusRingDanger
    ),
    link: cn(
      'h-auto min-h-0 px-0 py-0 text-[var(--fv-primary)] underline-offset-4',
      'hover:underline active:scale-100 shadow-none',
      'focus-visible:ring-offset-0'
    )
  };

  return cn(base, sizes[size], variants[variant], opts?.className);
}

export type IconButtonVariant = 'ghost' | 'danger';

export function iconButtonVariants(opts?: {
  variant?: IconButtonVariant;
  size?: 'sm' | 'md';
  className?: string;
}): string {
  const variant = opts?.variant ?? 'ghost';
  const size = opts?.size ?? 'md';

  return cn(
    'inline-flex shrink-0 items-center justify-center rounded-[var(--fv-radius)]',
    motion,
    press,
    'disabled:pointer-events-none disabled:opacity-50',
    focusRing,
    size === 'sm' ? 'h-7 w-7' : 'h-9 w-9',
    variant === 'ghost' &&
      'text-[var(--fv-text-muted)] hover:bg-[var(--fv-bg)] hover:text-[var(--fv-text)]',
    variant === 'danger' &&
      cn(
        'text-[var(--fv-danger)] hover:bg-[var(--fv-danger-muted)]',
        focusRingDanger
      ),
    opts?.className
  );
}

export function controlTriggerVariants(opts?: {
  tone?: 'default' | 'sidebar';
  open?: boolean;
  className?: string;
}): string {
  const tone = opts?.tone ?? 'default';
  const open = opts?.open ?? false;

  return cn(
    'flex h-10 w-full items-center justify-between gap-2 rounded-[var(--fv-radius)] border px-3 text-sm font-medium',
    motion,
    'disabled:cursor-not-allowed disabled:opacity-50',
    tone === 'sidebar'
      ? cn(
          'border-white/15 bg-white/10 text-[var(--fv-sidebar-text)]',
          'hover:border-white/25 hover:bg-white/15',
          'focus-visible:ring-2 focus-visible:ring-[var(--fv-primary-muted)]/40 focus-visible:ring-offset-0',
          open && 'border-[var(--fv-primary-muted)] ring-2 ring-[var(--fv-primary-muted)]/30'
        )
      : cn(
          focusRing,
          'border-[var(--fv-border)] bg-[var(--fv-surface)] text-[var(--fv-text)] shadow-[var(--fv-shadow-sm)]',
          'hover:border-[var(--fv-primary)]/40',
          open && 'border-[var(--fv-primary)] ring-2 ring-[var(--fv-primary-ring)]'
        ),
    opts?.className
  );
}

export function menuItemVariants(opts?: {
  highlight?: boolean;
  selected?: boolean;
  disabled?: boolean;
  className?: string;
}): string {
  return cn(
    'cursor-pointer px-3 py-2 text-sm transition-colors',
    opts?.disabled && 'cursor-not-allowed opacity-40',
    opts?.highlight && 'bg-[var(--fv-primary-muted)] text-[var(--fv-text)]',
    opts?.selected &&
      !opts?.highlight &&
      'font-medium text-[var(--fv-primary)]',
    !opts?.highlight && !opts?.selected && 'text-[var(--fv-text)] hover:bg-[var(--fv-bg)]',
    opts?.className
  );
}

export function segmentedShellVariants(className?: string): string {
  return cn(
    'flex gap-1 rounded-[var(--fv-radius)] border border-[var(--fv-border)] bg-[var(--fv-bg)] p-1 shadow-[var(--fv-shadow-sm)]',
    className
  );
}

export function segmentedItemVariants(opts?: {
  selected?: boolean;
  className?: string;
}): string {
  return cn(
    'flex-1 rounded-[calc(var(--fv-radius)-2px)] px-4 py-2 text-sm font-medium',
    motion,
    press,
    focusRing,
    opts?.selected
      ? cn(
          'bg-[var(--fv-primary-muted)] font-semibold text-[var(--fv-primary)] shadow-[var(--fv-shadow-sm)]',
          'ring-1 ring-[var(--fv-primary)]/20'
        )
      : 'text-[var(--fv-text-muted)] hover:bg-[var(--fv-surface)]/60 hover:text-[var(--fv-text)]',
    opts?.className
  );
}

export function sidebarNavItemVariants(opts?: {
  active?: boolean;
  disabled?: boolean;
  className?: string;
}): string {
  if (opts?.disabled) {
    return cn(
      'flex cursor-not-allowed items-center gap-3 rounded-[var(--fv-radius)] border-s-2 border-transparent px-3 py-2.5 text-sm text-[var(--fv-sidebar-muted)] opacity-60',
      opts.className
    );
  }
  return cn(
    'flex items-center gap-3 rounded-[var(--fv-radius)] border-s-2 px-3 py-2.5 text-sm transition-colors',
    motion,
    opts?.active
      ? 'border-[var(--fv-primary-muted)] bg-[var(--fv-sidebar-active)] font-medium text-white shadow-[var(--fv-shadow-sm)]'
      : 'border-transparent text-[var(--fv-sidebar-text)] hover:bg-white/10',
    opts?.className
  );
}

export function sidebarActionVariants(className?: string): string {
  return cn(
    buttonVariants({ variant: 'ghost', size: 'md', fullWidth: true }),
    'justify-start text-[var(--fv-sidebar-text)] hover:bg-white/10 hover:text-white',
    className
  );
}

export type ActionPillVariant = 'primary' | 'soft' | 'neutral' | 'danger' | 'success';

export function actionPillVariants(opts?: {
  variant?: ActionPillVariant;
  className?: string;
}): string {
  const variant = opts?.variant ?? 'soft';
  const base = cn(
    'inline-flex shrink-0 items-center justify-center rounded-full px-3 py-1 text-xs font-semibold',
    motion,
    press,
    'disabled:pointer-events-none disabled:opacity-50',
    focusRing
  );
  const variants: Record<ActionPillVariant, string> = {
    primary: cn(
      'bg-[var(--fv-primary)] text-white shadow-[var(--fv-shadow-button)]',
      'hover:bg-[var(--fv-primary-hover)]'
    ),
    soft: cn(
      'bg-[var(--fv-primary-muted)] text-[var(--fv-primary)] shadow-[var(--fv-shadow-sm)]',
      'hover:bg-[var(--fv-primary-muted)] hover:text-[var(--fv-primary-hover)]'
    ),
    neutral: cn(
      'border border-[var(--fv-border)] bg-[var(--fv-surface)] text-[var(--fv-text-muted)] shadow-[var(--fv-shadow-sm)]',
      'hover:bg-[var(--fv-bg)] hover:text-[var(--fv-text)]'
    ),
    danger: cn(
      'bg-[var(--fv-danger-muted)] text-[var(--fv-danger)] shadow-[var(--fv-shadow-sm)]',
      'hover:text-[var(--fv-danger-hover)]',
      focusRingDanger
    ),
    success: cn(
      'bg-[var(--fv-primary-muted)] text-[var(--fv-primary)] shadow-[var(--fv-shadow-sm)]',
      'hover:bg-[var(--fv-primary)] hover:text-white'
    )
  };
  return cn(base, variants[variant], opts?.className);
}

export function textLinkVariants(className?: string): string {
  return cn(
    'text-sm font-medium text-[var(--fv-primary)] underline-offset-4 transition-colors',
    'hover:text-[var(--fv-primary-hover)] hover:underline',
    'focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fv-primary-ring)]',
    className
  );
}

export function textLinkDangerVariants(className?: string): string {
  return cn(
    'text-sm font-medium text-[var(--fv-danger)] underline-offset-4 transition-colors',
    'hover:text-[var(--fv-danger-hover)] hover:underline',
    className
  );
}

export function choiceCardVariants(opts?: { selected?: boolean; className?: string }): string {
  return cn(
    'block cursor-pointer rounded-[var(--fv-radius-lg)] border px-4 py-3 text-start transition-[border-color,box-shadow,background-color]',
    motion,
    focusRing,
    opts?.selected
      ? 'border-[var(--fv-primary)] bg-[var(--fv-primary-muted)] shadow-[var(--fv-shadow-sm)] ring-1 ring-[var(--fv-primary)]/20'
      : 'border-[var(--fv-border)] bg-[var(--fv-surface)] hover:border-[var(--fv-primary)]/40 hover:shadow-[var(--fv-shadow-sm)]',
    opts?.className
  );
}

export function calendarDayVariants(opts?: {
  selected?: boolean;
  outside?: boolean;
  className?: string;
}): string {
  return cn(
    'h-9 w-9 rounded-[var(--fv-radius)] text-sm font-medium transition-colors',
    motion,
    focusRing,
    opts?.outside && 'text-[var(--fv-text-muted)] opacity-50',
    opts?.selected
      ? 'bg-[var(--fv-primary)] text-white shadow-[var(--fv-shadow-button)]'
      : 'text-[var(--fv-text)] hover:bg-[var(--fv-bg)]',
    opts?.className
  );
}
